import { useQuery, useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { Flex, toaster } from "@ui"
import { analytics } from "@utils/analytics"
import { useEffect, useState, type FC } from "react"

import { featureFlagsQuery } from "../-shared/featureFlags.query"
import { SaveOrCancelConfig } from "../-shared/SaveCancelConfig"
import type { SolutionSettings } from "../-shared/solutionSettings"
import {
  searchEngineListQuery,
  solutionSettingsQuery,
  solutionsListQuery,
} from "../-shared/solutionSettings.query"
import { useSolutionSettingsState } from "../-shared/useSolutionSettingsState"
import { queryClient } from "../../../queryClient"
import { AskCoPilot } from "./-components/AskCoPilot"
import { HandoverCard } from "./-components/Handover/HandoverCard"
import { PreviousConversations } from "./-components/PreviousConversations"

const SEARCH_ENGINE_DEBOUNCE_MS = 800
const SEARCH_ENGINE_INVALID_CREDENTIALS_STATUS = 409

const useDebouncedValue = <T,>(value: T, delay: number): T => {
  const [debouncedValue, setDebouncedValue] = useState(value)

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => {
      clearTimeout(timeout)
    }
  }, [delay, value])

  return debouncedValue
}

const BaseModulesPageContent: FC<{
  initialState: SolutionSettings
}> = ({ initialState }) => {
  const {
    draftState,
    patchPayload,
    isDirty,
    setDraftState,
    resetDraftState,
    updateInitialSettings,
  } = useSolutionSettingsState(initialState)
  const { askCoPilot } = draftState
  const hasSearchEngineCredentials = Boolean(
    askCoPilot?.url && askCoPilot.username && askCoPilot.password,
  )
  const liveCredentials = hasSearchEngineCredentials
    ? {
        url: askCoPilot?.url ?? "",
        username: askCoPilot?.username ?? "",
        password: askCoPilot?.password ?? "",
      }
    : null
  const debouncedCredentials = useDebouncedValue(
    liveCredentials,
    SEARCH_ENGINE_DEBOUNCE_MS,
  )
  const {
    data: searchEngineOptions,
    refetch: refetchSearchEngineList,
    isFetching: isSearchEngineLoading,
    error: searchEngineError,
    dataUpdatedAt: searchEngineDataUpdatedAt,
  } = useQuery({
    ...searchEngineListQuery({
      url: debouncedCredentials?.url ?? "",
      username: debouncedCredentials?.username ?? "",
      password: debouncedCredentials?.password ?? "",
    }),
    select: (data) =>
      data.searchEngines.map((searchEngine) => ({
        label: searchEngine.name,
        value: searchEngine.id.toString(),
      })),
    enabled: Boolean(debouncedCredentials),
  })

  useEffect(() => {
    if (!searchEngineError) {
      return
    }
    analytics.event("ask-copilot__search-engine-tested", {
      success: false,
      statusCode: searchEngineError.statusCode,
    })
    if (
      searchEngineError.statusCode === SEARCH_ENGINE_INVALID_CREDENTIALS_STATUS
    ) {
      toaster.create({
        title: "Ask Copilot credentials are invalid",
        description: "Please check your credentials and try again",
        type: "error",
      })
      return
    }
    toaster.create({
      title: "Failed to load search engines",
      description: searchEngineError.message,
      type: "error",
    })
  }, [searchEngineError])

  // dataUpdatedAt only advances on a successful fetch, so this fires once per
  // successful connection rather than on every render the mapped options change.
  const searchEngineCount = searchEngineOptions?.length ?? 0
  useEffect(() => {
    if (!searchEngineDataUpdatedAt) {
      return
    }
    analytics.event("ask-copilot__search-engine-tested", {
      success: true,
      engineCount: searchEngineCount,
    })
  }, [searchEngineDataUpdatedAt, searchEngineCount])

  const handleSearchEnginesRetry = () => {
    if (!debouncedCredentials) {
      return
    }
    //Still not sure how I feel about this void to explicitly disregard the promise.
    void refetchSearchEngineList()
  }
  return (
    <SaveOrCancelConfig
      title="Base Modules"
      patchPayload={patchPayload}
      onReset={resetDraftState}
      onSave={updateInitialSettings}
      isDisabled={!isDirty}
    >
      <Flex
        flexDirection="column"
        gap="lg"
        minWidth="none"
      >
        <PreviousConversations
          checked={draftState.modules.previousInteractions}
          onStateChange={setDraftState}
          previousInteractions={draftState.previousInteractions}
          locked={!draftState.modulesAccess.previousInteractions}
        />
        <HandoverCard
          checked={draftState.modules.handover}
          onStateChange={setDraftState}
          handover={draftState.handover}
          handoverAccess={draftState.modulesAccess.handover}
          voiceBotHandover={draftState.modules.voiceBotHandover}
          voiceBotHandoverAccess={draftState.modulesAccess.voiceBotHandover}
        />
        <AskCoPilot
          checked={draftState.modules.askCopilot}
          onStateChange={setDraftState}
          askCoPilot={askCoPilot}
          hasSearchEngineError={Boolean(searchEngineError)}
          onSearchEnginesRetry={handleSearchEnginesRetry}
          searchEngineOptions={searchEngineOptions}
          isSearchEngineLoading={isSearchEngineLoading}
          locked={!draftState.modulesAccess.askCopilot}
        />
      </Flex>
    </SaveOrCancelConfig>
  )
}

const BaseModulesPage: FC = () => {
  const { data: solutionSettingsData } = useSuspenseQuery(solutionSettingsQuery)
  return (
    <BaseModulesPageContent
      key={solutionSettingsData.solutionUid}
      initialState={solutionSettingsData}
    />
  )
}

export const Route = createFileRoute("/_authenticated/base-modules/")({
  loader: () => {
    return Promise.all([
      queryClient.ensureQueryData(solutionSettingsQuery),
      queryClient.ensureQueryData(solutionsListQuery),
      queryClient.ensureQueryData(featureFlagsQuery),
    ])
  },
  component: BaseModulesPage,
})
